import { isEmpty } from '@fruitful/fruitils';
import {
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountUpsertDto,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapCategory,
    type MoneyMapRuleUpsertDto,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
} from '@fruitful/types';

import type { MembershipDetails } from '../../entities/entityTypes';
import {
    type AccountSettingsFormSchema,
    type ApexCreateParametersVariant,
    extractCreateParametersFromAccountSettingsToSubmit,
    type TransferAccountSettingsFormSchema,
} from '../components/forms/AccountSettingsFormModule';
import { shouldUseTransferAccount } from './moneyMapUtils';

interface BuildMoneyMapAccountUpsertDtoOptions {
    apexVariant?: ApexCreateParametersVariant;
    skipTransferAccount?: boolean;
}

const wrapExistingAccountSelectionAsUpsertDto = (
    moneyMapAccountId: string | undefined,
    selectedAccount: MoneyMapAccountWithAccountDetails | undefined,
    membershipId: string,
): MoneyMapAccountUpsertDto | undefined => {
    if (
        moneyMapAccountId == null ||
        moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value ||
        moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value ||
        moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value
    ) {
        return undefined;
    }

    if (selectedAccount == null) {
        return undefined;
    }

    return {
        id: selectedAccount.id,
        membershipId,
        type: selectedAccount.type,
        status: selectedAccount.status,
    };
};

// TODO - DRY this method up.
// TODO - Use this util in IncomeForm.
export const buildMoneyMapAccountForMoneyMapRuleUpsertDto = (
    moneyMapRuleUpsertDto: MoneyMapRuleUpsertDto,
    { accountSettings }: AccountSettingsFormSchema,
    { transferAccountSettings }: TransferAccountSettingsFormSchema,
    moneyMapCategory: MoneyMapCategory,
    membership: MembershipDetails | null,
    selectedMoneyMapAccount?: MoneyMapAccountWithAccountDetails,
    proposedAccountToUpdate?: MoneyMapAccountWithAccountDetails,
    proposedTransferAccountToUpdate?: MoneyMapAccountWithAccountDetails,
    moneyMapIncomeAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>,
    ruleOptions?: { isBackdoorRoth?: boolean },
    selectedBackdoorTradAccount?: MoneyMapAccountWithAccountDetails,
): MoneyMapRuleUpsertDto => {
    if (membership == null) {
        return moneyMapRuleUpsertDto;
    }

    if (accountSettings.moneyMapAccountId == null) {
        return moneyMapRuleUpsertDto;
    }

    if (ruleOptions?.isBackdoorRoth === true) {
        if (transferAccountSettings?.moneyMapAccountId == null) {
            return moneyMapRuleUpsertDto;
        }

        const tradDto =
            buildMoneyMapAccountUpsertDto(
                { accountSettings: transferAccountSettings },
                { transferAccountSettings: undefined },
                moneyMapCategory,
                membership,
                selectedBackdoorTradAccount,
                proposedTransferAccountToUpdate,
                undefined,
                moneyMapIncomeAccount,
                {
                    apexVariant: 'backdoorTraditional',
                    skipTransferAccount: true,
                },
            ) ??
            wrapExistingAccountSelectionAsUpsertDto(
                transferAccountSettings.moneyMapAccountId,
                selectedBackdoorTradAccount,
                membership.id,
            );

        const rothDto =
            buildMoneyMapAccountUpsertDto(
                { accountSettings },
                { transferAccountSettings: undefined },
                moneyMapCategory,
                membership,
                selectedMoneyMapAccount,
                proposedAccountToUpdate,
                undefined,
                moneyMapIncomeAccount,
                {
                    apexVariant: 'backdoorRoth',
                    skipTransferAccount: true,
                },
            ) ??
            wrapExistingAccountSelectionAsUpsertDto(
                accountSettings.moneyMapAccountId,
                selectedMoneyMapAccount,
                membership.id,
            );

        // Never persist a backdoor rule with only one leg: omitting Roth used to send
        // `backdoorRothLinkedMoneyMapAccount: undefined`, which can clear an existing pair.
        if (tradDto == null || rothDto == null) {
            return moneyMapRuleUpsertDto;
        }

        moneyMapRuleUpsertDto.moneyMapAccount = {
            ...tradDto,
            backdoorRothLinkedMoneyMapAccount: rothDto,
        };

        return moneyMapRuleUpsertDto;
    }

    const moneyMapAccountUpsertDto = buildMoneyMapAccountUpsertDto(
        { accountSettings },
        { transferAccountSettings },
        moneyMapCategory,
        membership,
        selectedMoneyMapAccount,
        proposedAccountToUpdate,
        proposedTransferAccountToUpdate,
        moneyMapIncomeAccount,
    );

    if (moneyMapAccountUpsertDto != null) {
        moneyMapRuleUpsertDto.moneyMapAccount = moneyMapAccountUpsertDto;
    } else {
        moneyMapRuleUpsertDto.moneyMapAccountId =
            accountSettings.moneyMapAccountId;
    }

    return moneyMapRuleUpsertDto;
};

const buildMoneyMapAccountUpsertDto = (
    { accountSettings }: AccountSettingsFormSchema,
    { transferAccountSettings }: TransferAccountSettingsFormSchema,
    moneyMapCategory: MoneyMapCategory,
    membership: MembershipDetails | null,
    selectedMoneyMapAccount?: MoneyMapAccountWithAccountDetails,
    proposedAccountToUpdate?: MoneyMapAccountWithAccountDetails,
    proposedTransferAccountToUpdate?: MoneyMapAccountWithAccountDetails,
    moneyMapIncomeAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>,
    buildOptions: BuildMoneyMapAccountUpsertDtoOptions = {},
): MoneyMapAccountUpsertDto | undefined => {
    if (membership == null) {
        return undefined;
    }

    if (accountSettings.moneyMapAccountId == null) {
        return undefined;
    }

    const apexVariant: ApexCreateParametersVariant =
        buildOptions.apexVariant ?? 'standard';

    const isNewCashAccount =
        accountSettings.moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;

    const isNewIMAccount =
        accountSettings.moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value;
    const isNewApexAccount =
        accountSettings.moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;

    const isUpdatingExistingProposedAccount = proposedAccountToUpdate != null;

    const isProposingNewCashAccountForTransfer =
        transferAccountSettings?.moneyMapAccountId ===
        NEW_CASH_ACCOUNT_OPTION.value;

    const isUpdatingExistingProposedAccountForTransfer =
        proposedTransferAccountToUpdate != null;

    let upsertDto: MoneyMapAccountUpsertDto | undefined;

    // An account upsert dto will be required if: 1) Creating a new money map account, or 2) Updating an existing proposed account.
    if (isNewCashAccount || isNewIMAccount || isNewApexAccount) {
        const accountType = isNewCashAccount
            ? MoneyMapAccountType.INTERNAL_BANKING
            : isNewIMAccount
              ? MoneyMapAccountType.INVESTMENT_MANAGEMENT
              : MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

        // Create a proposed money map account.
        upsertDto = {
            membershipId: membership.id,
            type: accountType,
            status: MoneyMapAccountStatus.PROPOSED,
            createParameters:
                extractCreateParametersFromAccountSettingsToSubmit(
                    { accountSettings },
                    moneyMapCategory,
                    accountType,
                    apexVariant,
                ),
        };
    } else if (isUpdatingExistingProposedAccount) {
        // Update a proposed money map account.
        upsertDto = {
            ...proposedAccountToUpdate, // Include the id if updating an existing, proposed MoneyMapAccount.
            createParameters:
                extractCreateParametersFromAccountSettingsToSubmit(
                    { accountSettings },
                    moneyMapCategory,
                    // We only ever allow proposing banking or IM accounts, this cast should be okay
                    proposedAccountToUpdate.type as
                        | MoneyMapAccountType.INTERNAL_BANKING
                        | MoneyMapAccountType.INVESTMENT_MANAGEMENT
                        | MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
                    apexVariant,
                ),
        };
    }

    // An upsert dto will also need to be provided if the account requires a transfer account, since the transfer account data is only provided as part of the upsert account request.
    if (
        buildOptions.skipTransferAccount !== true &&
        shouldUseTransferAccount(
            selectedMoneyMapAccount,
            accountSettings.moneyMapAccountId,
            moneyMapIncomeAccount,
        )
    ) {
        // If the upsertDto is undefined, then an existing non-proposed account was selected and we add its details to satisfy the dto type.
        if (upsertDto == null) {
            upsertDto = {
                ...(selectedMoneyMapAccount as MoneyMapAccountWithAccountDetails), // Non-null type-check is performed in accountRequiresTransferAccount.
            };
        }

        if (isProposingNewCashAccountForTransfer) {
            // Create a proposed money map account as the transfer account.
            upsertDto = {
                ...upsertDto,
                moneyMapAccountIdForTransfer: undefined,
                moneyMapAccountForTransfer: {
                    membershipId: membership.id,
                    type: MoneyMapAccountType.INTERNAL_BANKING, // Only allow proposing bank accounts for transfer accounts.
                    status: MoneyMapAccountStatus.PROPOSED,
                    createParameters:
                        extractCreateParametersFromAccountSettingsToSubmit(
                            { transferAccountSettings },
                            moneyMapCategory,
                            MoneyMapAccountType.INTERNAL_BANKING, // Only allow proposing bank accounts for transfer accounts.
                        ),
                },
            };
        } else if (isUpdatingExistingProposedAccountForTransfer) {
            // Update a proposed money map account.
            upsertDto = {
                ...upsertDto,
                moneyMapAccountIdForTransfer: undefined,
                moneyMapAccountForTransfer: {
                    ...proposedTransferAccountToUpdate,
                    createParameters:
                        extractCreateParametersFromAccountSettingsToSubmit(
                            { transferAccountSettings },
                            moneyMapCategory,
                            // We only ever allow proposing bank accounts for transfer accounts, so type cast should be okay.
                            proposedTransferAccountToUpdate.type as MoneyMapAccountType.INTERNAL_BANKING,
                        ),
                },
            };
        } else if (!isEmpty(transferAccountSettings?.moneyMapAccountId)) {
            // Selecting an existing money map account to set as transfer account
            upsertDto = {
                ...upsertDto,
                moneyMapAccountIdForTransfer:
                    transferAccountSettings?.moneyMapAccountId,
            };
        }
    }

    return upsertDto;
};
